'use client';

import { c as _c } from "react/compiler-runtime";
import './calendar.scss';
import { ComponentProps, FC, useEffect, useRef } from 'react';
import { DateLib, DayButton, DayPicker, DayPickerProps, Dropdown, Locale } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { ButtonStyle } from '../../atoms/button-style/button-style.tsx';
import { cn } from '../../utils/helpers.ts';
import { Select } from '../select/select.tsx';
export interface CalendarProps {
  className?: string;
  showOutsideDays?: boolean;
}
const getTranslationKey = (key: string): string => {
  return `core:calendar.${key}`;
};
const CalendarDayButton: FC<ComponentProps<typeof DayButton>> = t0 => {
  const $ = _c(21);
  let day;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      day,
      modifiers,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = day;
    $[2] = modifiers;
    $[3] = props;
  } else {
    day = $[1];
    modifiers = $[2];
    props = $[3];
  }
  const {
    selected,
    focused,
    disabled,
    range_start: rangeStart,
    range_end: rangeEnd,
    range_middle: rangeMiddle
  } = modifiers;
  const ref = useRef(null);
  let t1;
  let t2;
  if ($[4] !== focused) {
    t1 = () => {
      if (focused) {
        ref.current?.focus();
      }
    };
    t2 = [focused];
    $[4] = focused;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  const t3 = selected ? "primary" : "tertiary";
  const t4 = selected ? "positive" : "neutral";
  let t5;
  if ($[7] !== day.date) {
    t5 = day.date.toLocaleDateString();
    $[7] = day.date;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = selected && !rangeStart && !rangeEnd && !rangeMiddle;
  let t7;
  if ($[9] !== props || $[10] !== rangeEnd || $[11] !== rangeMiddle || $[12] !== rangeStart || $[13] !== t5 || $[14] !== t6) {
    t7 = <button ref={ref} type="button" data-day={t5} data-selected-single={t6} data-range-start={rangeStart} data-range-end={rangeEnd} data-range-middle={rangeMiddle} {...props} />;
    $[9] = props;
    $[10] = rangeEnd;
    $[11] = rangeMiddle;
    $[12] = rangeStart;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== disabled || $[17] !== t3 || $[18] !== t4 || $[19] !== t7) {
    t8 = <ButtonStyle size="md" variant={t3} purpose={t4} disabled={disabled} className="Calendar__day-button label-md">{t7}</ButtonStyle>;
    $[16] = disabled;
    $[17] = t3;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
const CalendarSelect: FC<{
  name: string;
} & ComponentProps<typeof Dropdown>> = t0 => {
  const $ = _c(18);
  let name;
  let options;
  let props;
  if ($[0] !== t0) {
    ({
      name,
      options,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = name;
    $[2] = options;
    $[3] = props;
  } else {
    name = $[1];
    options = $[2];
    props = $[3];
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[4] !== name || $[5] !== options) {
    const selectOptions = options ?? [];
    T0 = Select;
    t1 = name;
    t2 = "mx-snug-lg";
    t3 = "Calendar__select";
    t4 = selectOptions?.filter(_temp).map(_temp2);
    $[4] = name;
    $[5] = options;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== T0 || $[12] !== props || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <T0 name={t1} fieldClasses={t2} selectClasses={t3} items={t4} {...props} />;
    $[11] = T0;
    $[12] = props;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export const Calendar: FC<DayPickerProps> = t0 => {
  const $ = _c(28);
  let calendarLocal;
  let className;
  let formatters;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      formatters,
      locale: calendarLocal,
      showOutsideDays: t1,
      captionLayout: t2,
      autoFocus: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = calendarLocal;
    $[2] = className;
    $[3] = formatters;
    $[4] = props;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    calendarLocal = $[1];
    className = $[2];
    formatters = $[3];
    props = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  const showOutsideDays = t1 === undefined ? true : t1;
  const captionLayout = t2 === undefined ? "dropdown" : t2;
  const autoFocus = t3 === undefined ? true : t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["core"];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    t
  } = useTranslation(t4);
  let t5;
  if ($[9] !== className) {
    t5 = cn("p-comfy-md", className);
    $[9] = className;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== calendarLocal) {
    t6 = month => {
      const dateLib = new DateLib({
        locale: calendarLocal as Locale
      });
      return dateLib.format(month, "LLL");
    };
    $[11] = calendarLocal;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== formatters || $[14] !== t6) {
    t7 = {
      formatMonthDropdown: t6,
      ...formatters
    };
    $[13] = formatters;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = {
      labelNext: () => t(getTranslationKey("nextButtonAriaLabel")),
      labelPrevious: () => t(getTranslationKey("previousButtonAriaLabel")),
      labelMonthDropdown: () => t(getTranslationKey("monthSelectAriaLabel")),
      labelYearDropdown: () => t(getTranslationKey("yearSelectAriaLabel"))
    };
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      months: "flex flex-col md:flex-row relative",
      month: "flex flex-col w-full gap-4",
      nav: "flex absolute w-full justify-between",
      button_previous: "Calendar__nav-button flex align-center p-snug-xl",
      button_next: "Calendar__nav-button flex align-center p-snug-xl",
      month_caption: "Calendar__month flex items-center justify-center",
      month_grid: "w-full border-collapse",
      dropdowns: "flex items-center justify-center",
      weekdays: "flex mb-snug-lg mt-comfy-lg",
      weekday: "Calendar__weekday flex-1 label-md label-subtle p-snug-xs",
      week: "flex w-full mt-2",
      day: "Calendar__day relative w-full text-center p-snug-xs",
      day_button: "w-full",
      today: cn("Calendar__day--today"),
      selected: cn("Calendar__day--selected"),
      outside: cn("Calendar__day--outside"),
      disabled: cn("Calendar__day--disabled")
    };
    t10 = {
      YearsDropdown: _temp3,
      MonthsDropdown: _temp4,
      Chevron: _temp5,
      DayButton: _temp6
    };
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== autoFocus || $[21] !== captionLayout || $[22] !== props || $[23] !== showOutsideDays || $[24] !== t5 || $[25] !== t7 || $[26] !== t8) {
    t11 = <DayPicker showOutsideDays={showOutsideDays} className={t5} captionLayout={captionLayout} formatters={t7} labels={t8} classNames={t9} components={t10} autoFocus={autoFocus} {...props} />;
    $[20] = autoFocus;
    $[21] = captionLayout;
    $[22] = props;
    $[23] = showOutsideDays;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp(option) {
  return !option.disabled;
}
function _temp2(t0) {
  const {
    value,
    label
  } = t0;
  return {
    value: value.toString(),
    text: label
  };
}
function _temp3(dropdownProps) {
  return <CalendarSelect name="years" {...dropdownProps} />;
}
function _temp4(dropdownProps_0) {
  return <CalendarSelect name="months" {...dropdownProps_0} />;
}
function _temp5(t0) {
  const {
    orientation,
    disabled
  } = t0;
  return <ArkIcon iconName={`chevron-${orientation ?? "down"}`} size="1.5em" tabIndex={disabled ? -1 : 0} />;
}
function _temp6(buttonProps) {
  return <CalendarDayButton {...buttonProps} />;
}